import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { CELL_FIRE_TUNING } from "../tuning/featureTuning";
import type { Point } from "./flightPath";
import { ART_SCALE, CELL_FIRE_LAYOUT } from "./layout";

/**
 * The artist's cell hit (`cell_fire.riv`'s `CellHit`, played as its Spine conversion): its `hit` trigger plays a fire
 * burst once (0.8 s; again mid-burst, from its start): the cell flashes warm, a white-hot fireball rolls out into a ring
 * of licking flames that breaks apart, round embers float off. Idle, it draws nothing.
 */
export const CELL_HIT_RIVE = { trigger: "hit" } as const;

/** What a cell hit asks of its art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface CellHitArt {
  readonly texture: Texture;
  resize(scale: number): void;
  trigger(name: string): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

interface Hit {
  art: CellHitArt;
  sprite: Sprite;
  /** Engine seconds of its burst still to draw (0: idle, hidden). */
  left: number;
  /** When it last burst, by count: with none idle, the one bursting longest starts over. */
  order: number;
}

/**
 * The bursts as MULTI's fireballs reach their cells (the artist's handoff): a few, played on a cell's centre at the
 * mock-up's scale, over the board and its coins, under the multiplier's number (it rides the streak's layer, over this
 * one). Each shown and drawn on the UI's runner for its burst at the tuning's frame rate, then hidden: never work a
 * round waits for. Nothing plays before they are in.
 */
export class CellHits extends Container {
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private hits: Hit[] | null = null;
  private played = 0;
  private drawing = false;
  /** Device pixels per design unit where they play (their textures' size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { ambientRunner: PresentationTaskRunner; log: Log }) {
    super({ label: "CellHits" });
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
  }

  /** The artist's hits, loaded: each centred on its point, hidden until it plays. */
  public setArt(arts: readonly CellHitArt[]): void {
    if (this.destroyed || this.hits) {
      for (const art of arts) art.dispose();
      return;
    }
    const { width, height } = CELL_FIRE_LAYOUT.hit;
    this.hits = arts.map((art) => {
      art.resize(this.textureScale());
      const sprite = new Sprite({ texture: art.texture, anchor: 0.5, label: "CellHit" });
      sprite.setSize(width * ART_SCALE, height * ART_SCALE);
      sprite.visible = false;
      this.addChild(sprite);
      return { art, sprite, left: 0, order: 0 };
    });
    this.log("[CellHits] the cell hits are in");
  }

  /** Whether they are in. */
  public get ready(): boolean {
    return this.hits !== null;
  }

  /** Whether they are in, how many have burst, and how many burst now (the dev hook's read). */
  public get state(): { loaded: boolean; hits: number; playing: number } {
    return { loaded: this.hits !== null, hits: this.played, playing: this.hits?.filter((hit) => hit.left > 0).length ?? 0 };
  }

  /** Device pixels per design unit where they play: their textures are drawn at their size on the screen. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    for (const hit of this.hits ?? []) hit.art.resize(this.textureScale());
  }

  /** A hit bursts on `at` (this layer's space): an idle one, else the one bursting longest, from its start. */
  public play(at: Point): void {
    const hits = this.hits;
    if (!hits || this.destroyed) return;
    const hit = hits.find((one) => one.left <= 0) ?? [...hits].sort((a, b) => a.order - b.order)[0];
    if (!hit) return;
    this.played += 1;
    hit.order = this.played;
    hit.sprite.position.set(at.x, at.y);
    hit.art.trigger(CELL_HIT_RIVE.trigger);
    hit.art.render(0);
    hit.sprite.visible = true;
    hit.left = CELL_FIRE_TUNING.hitSeconds;
    this.draw();
  }

  public override destroy(options?: DestroyOptions): void {
    const hits = this.hits;
    this.hits = null;
    for (const hit of hits ?? []) hit.art.dispose();
    super.destroy(options);
  }

  /** Texture pixels per artboard pixel: its size on the screen, at the tuning's share. */
  private textureScale(): number {
    return ART_SCALE * this.pixelsPerUnit * CELL_FIRE_TUNING.resolution;
  }

  /** The bursting hits drawn at their own pace until each is over, then hidden. */
  private draw(): void {
    if (this.drawing) return;
    this.drawing = true;
    let last = 0;
    const bursting = (): boolean => this.hits?.some((hit) => hit.left > 0) ?? false;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          const delta = elapsed - last;
          last = elapsed;
          for (const hit of this.hits ?? []) {
            if (hit.left <= 0) continue;
            hit.art.advance(delta);
            hit.left = Math.max(0, hit.left - delta);
            if (hit.left === 0) hit.sprite.visible = false;
          }
        },
        () => this.destroyed || !bursting(),
      )
      .then(() => {
        this.drawing = false;
        // One burst as the last ended.
        if (!this.destroyed && bursting()) this.draw();
      });
  }
}
